/**
 * `ASSIGN_MASTER` step. Operates on CSE ids — on-request rows are
 * structurally rejected (their `cseId` is `null`). Uses `skipInvalidate:true`;
 * the executor's terminal INVALIDATE step is the single cache flush.
 */
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { CseId, DayOfWeek, RouteId, ServiceDate } from 'types/entities/shared/ids'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type {
  AssignMasterResponse,
  Deps,
  PipelineStep,
} from 'types/modules/routing'

import { buildSequencePairs } from '../payloads'
import type { StepResult } from '../types'

const BATCH_SIZE = 200

export interface AssignMasterStepArgs {
  readonly routeId: RouteId
  readonly dayOfWeek: DayOfWeek
  readonly cseIds: ReadonlyArray<CseId>
  readonly futureStops?: boolean
  readonly futureStart?: ServiceDate
  readonly startSequence?: number
}

export type AssignMasterStepValue = {
  readonly stopCount: number
  readonly batchCount: number
}

export type AssignMasterStepResult = StepResult<'ASSIGN_MASTER', AssignMasterStepValue>

export const makeAssignMasterStep = (
  args: AssignMasterStepArgs,
  deps: Deps,
): PipelineStep<'ASSIGN_MASTER', AssignMasterStepResult> => {
  const stepId = `assign-master:${args.routeId}:${args.dayOfWeek}`
  const futureStops = args.futureStops ?? true
  const dayOfWeek = args.dayOfWeek as ApiDayOfWeek
  return {
    id: stepId,
    phase: 'ASSIGN_MASTER',
    label: 'Failed to assign stops to master sequence',
    applyOrder: 300,
    run: async (ctx) => {
      if (args.cseIds.length === 0) {
        return ok({
          phase: 'ASSIGN_MASTER',
          stepId,
          value: { stopCount: 0, batchCount: 0 },
        })
      }
      const fullPairs = buildSequencePairs(
        args.cseIds.map(id => ({ id: String(id) })),
        'id',
        args.startSequence ?? 1,
      ).pairs
      const batches = chunk(fullPairs, BATCH_SIZE)
      let stopCount = 0
      try {
        for (const batch of batches) {
          const response: AssignMasterResponse = await deps
            .assignMaster({
              routeId: args.routeId,
              data: {
                dayOfWeek,
                futureStops,
                futureStart: args.futureStart ?? null,
                stops: batch.map(p => ({ stopId: String(p.stopId), sequence: p.sequence })),
                versionContext: ctx.versionContext,
              },
              skipInvalidate: true,
            })
            .unwrap()
          stopCount += response?.stopCount ?? 0
        }
        return ok({
          phase: 'ASSIGN_MASTER',
          stepId,
          value: { stopCount, batchCount: batches.length },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'ASSIGN_MASTER',
          status,
          message,
        })
      }
    },
  }
}
